import { useEffect, useState } from 'react';
import { createDocValueStore } from './docStore';
import type { CatalogEntry } from './catalogItems';
import type { HardwareInteraction } from './hardwareInteractionsRun';
import { writtenPartName } from './openaiBlenderApi';
import { partCatalogId } from './partIdentity';
import type { PartItem } from './partsRun';
import { fetchSetJobs, paintParts, setKeyOf, type Job3D, type PieceItem } from './threeDApi';
import { readCatalogItems } from './catalogItems';
import { getHardwareInteractions } from './hardwareInteractionsRun';
import { getPartMaterials } from './partMaterialRun';
import { getParts } from './partsRun';
import { surfaceOf } from './materialSurface';
import type { Material } from './materialsRun';
import { materialText } from './variantLabel';

// A HARDWARE SET: one row of Phase 4's hardware table, built as ONE 3D model — the screw, its
// washer and the barrel nut it goes into, fitted together as they are in use — and cut into
// one model per part when kept. Built together, they are oriented and sized against each other
// by one decision, where built apart they could only follow the same rule and hope.

export type HardwareSet = {
  // The parts' catalog ids, sorted and joined — the server's key for the set (see _set_mark).
  key: string;
  // Each part as it is built and filed: the name the build calls its object, and its row.
  parts: (PieceItem & { at: number })[];
};

// The set a table row is, or null where fewer than two of its parts have a catalog row — there
// would be nowhere to file one of the pieces.
export function setOfInteraction(
  one: HardwareInteraction, items: PartItem[], catalog: CatalogEntry[], loaded: boolean,
): HardwareSet | null {
  const parts = one.hardware.flatMap((id) => {
    const at = Number(id);
    const p = items[at];
    const itemId = p ? partCatalogId(catalog, loaded, p) : null;
    return p && itemId != null ? [{ name: writtenPartName(p), itemId, at }] : [];
  });
  if (parts.length < 2) return null;
  return { key: parts.map((p) => p.itemId).sort((a, b) => a - b).join('-'), parts };
}

// A SET'S NAME: its parts joined, those made under Tools last, each an addition in its own brackets —
// "Spring washer + Wood button (+ JCBC screw) (+ Barrel nut)". In Hardware & covers they come into
// the set made, and it builds the rest around them (see notice_3d._reused).
export function setName<P>(parts: P[], nameOf: (p: P) => string, fromTools: (p: P) => boolean): string {
  const own = parts.filter((p) => !fromTools(p)).map(nameOf).join(' + ');
  const made = parts.filter(fromTools).map((p) => `(+ ${nameOf(p)})`).join(' ');
  return [own, made].filter(Boolean).join(' ');
}

// EVERY SET THIS MANUAL HOLDS, deduped by key and in the table's order — the rows that can be
// built (see setOfInteraction), each with the row it came from.
export function setsOf(
  interactions: HardwareInteraction[], items: PartItem[], catalog: CatalogEntry[], loaded: boolean,
): { set: HardwareSet; one: HardwareInteraction }[] {
  const out: { set: HardwareSet; one: HardwareInteraction }[] = [];
  for (const one of interactions) {
    const set = setOfInteraction(one, items, catalog, loaded);
    if (set && !out.some((had) => had.set.key === set.key)) out.push({ set, one });
  }
  return out;
}

// THIS MANUAL'S SET RUNS, re-read on a bump — the product's arrangement (see productRuns): the
// panel that makes them is thrown away and drawn again, and the list must follow a run it made.
const epochStore = createDocValueStore<number>(0);
export const bumpSetRuns = (docId: string) => epochStore.set(docId, epochStore.get(docId) + 1);

// The last answer per document and set, so a remount does not spin again.
const lastSetRuns = new Map<string, Job3D[]>();

// `runs` is NULL UNTIL THE SERVER HAS ANSWERED — `[]` is "never built", and it stood in for
// "not asked yet" too. `failed`: the last ask did not come back; what was known of it stays.
export function useSetRuns(docId: string, key: string): { runs: Job3D[] | null; failed: boolean } {
  const epoch = epochStore.useValue(docId);
  const at = `${docId}|${key}`;
  const [state, setState] = useState<{ at: string; runs: Job3D[] | null; failed: boolean }>(
    () => ({ at, runs: lastSetRuns.get(at) ?? null, failed: false }),
  );
  useEffect(() => {
    let alive = true;
    fetchSetJobs(docId)
      .then((jobs) => {
        const runs = jobs.filter((j) => setKeyOf(j) === key);
        lastSetRuns.set(at, runs);
        if (alive) setState({ at, runs, failed: false });
      })
      .catch(() => {
        if (alive) setState((was) => ({ at, runs: was.at === at ? was.runs : lastSetRuns.get(at) ?? null, failed: true }));
      });
    return () => { alive = false; };
  }, [docId, key, epoch, at]);
  // Another set's answer is not this one's: until this one's lands, what is known of it.
  return state.at === at ? state : { runs: lastSetRuns.get(at) ?? null, failed: false };
}

// EVERY SET RUN OF THIS MANUAL, each with every part's box in its own frame — what places two
// pieces cut from different sets (see setPlacement). Empty until the server has answered, which
// is "no set to place by" for that moment, not a claim; re-read on a bump.
const lastManualSets = new Map<string, Job3D[]>();
// WHETHER THIS MANUAL'S SETS HAVE BEEN READ YET: until then useManualSets answers [], which reads
// as "no set holds these two" — and a rule run in that moment works out a tool riding nothing.
export const manualSetsKnown = (docId: string): boolean => lastManualSets.has(docId);

export function useManualSets(docId: string): Job3D[] {
  const epoch = epochStore.useValue(docId);
  const [sets, setSets] = useState<Job3D[]>(() => lastManualSets.get(docId) ?? []);
  useEffect(() => {
    let alive = true;
    fetchSetJobs(docId)
      .then((jobs) => {
        lastManualSets.set(docId, jobs);
        if (alive) setSets(jobs);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [docId, epoch]);
  return sets;
}

// WHICH OF THIS MANUAL'S SETS HAVE A MODEL IN THE DATABASE, by key — a set kept, whose pieces are
// its parts' models. Re-read on a bump, as the set runs are.
export function useKeptSetKeys(docId: string): Set<string> {
  const epoch = epochStore.useValue(docId);
  const [kept, setKept] = useState<Set<string>>(new Set());
  useEffect(() => {
    let alive = true;
    fetchSetJobs(docId)
      .then((jobs) => {
        if (alive) setKept(new Set(jobs.flatMap((j) => (j.inCatalog && setKeyOf(j) ? [setKeyOf(j) as string] : []))));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [docId, epoch]);
  return kept;
}

// WHAT EACH PART OF A SET IS MADE OF, off Phase 3's table, as the build paints it on object by
// object (see build_code's `paint`) — never a question put to the model. A part whose material
// has no colour yet — or no roughness and metallic, which are judged too — is named in
// `uncoloured` rather than painted a surface nobody chose.
//
// ONE RULE, two callers: the set's build (HardwareSetStep), and the repaint when a reading is
// applied (repaintSets) — so a model built today and a model repainted tomorrow wear the same.
export function setPaint(docId: string, submodel: string, set: HardwareSet) {
  const byPart = getPartMaterials(docId).bySub[submodel];
  const paint: { part: string; material: Material }[] = [];
  const uncoloured: string[] = [];
  for (const p of set.parts) {
    const pair = byPart?.[p.at];
    const surface = pair ? surfaceOf(docId, pair) : null;
    if (pair && surface?.rgb && surface.roughness != null && surface.metallic != null) {
      paint.push({ part: p.name, material: {
        name: materialText(pair), rgb: surface.rgb, roughness: surface.roughness, metallic: surface.metallic,
        ...(surface.texture ? { texture: surface.texture } : {}),
      } });
    } else {
      uncoloured.push(p.name);
    }
  }
  return { paint, uncoloured };
}

// EVERY KEPT SET MODEL REPAINTED to the materials applied now — called when a reading is applied
// (see applyMaterials), which repaints the product's and the parts' kept meshes on the server
// and never reached these: they are filed under the set's mark, not the product's or a part's.
// A set model then always wears the applied materials, as every other mesh does, and the pieces
// cut from it follow (see paint-parts).
export async function repaintSets(docId: string, submodel: string): Promise<void> {
  const catalog = await readCatalogItems();
  const sets = setsOf(getHardwareInteractions(docId).interactions, getParts(docId), catalog, true);
  if (!sets.length) return;
  const jobs = (await fetchSetJobs(docId)).filter((j) => j.inCatalog);
  let painted = false;
  for (const job of jobs) {
    const found = sets.find(({ set }) => set.key === setKeyOf(job));
    if (!found) continue;
    const { paint } = setPaint(docId, submodel, found.set);
    if (!paint.length) continue;
    await paintParts(job.id, paint);
    painted = true;
  }
  if (painted) bumpSetRuns(docId);
}
